import React, { useEffect, useState, useCallback } from 'react';
import { FaAngleRight, FaAngleLeft } from "react-icons/fa6";
import { summaryApi } from '../../common';

function BannerProduct() {
    const [currentImage, setCurrentImage] = useState(0);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch banners from API
    useEffect(() => {
        const fetchBanners = async () => {
            try {
                setLoading(true);
                const response = await fetch(summaryApi.getBanners.url, {
                    method: summaryApi.getBanners.method,
                    
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setBanners(data.banners);
                    }
                }
            } catch (error) {
                console.error('Error fetching banners:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBanners();
    }, []);

    const desktopImages = banners.map(banner => banner.desktopImage.url);
    const mobileImages = banners.map(banner => banner.mobileImage.url);

    const handleLeftClick = useCallback(() => {
        setCurrentImage(prev => 
            prev === 0 ? banners.length - 1 : prev - 1
        );
    }, [banners.length]);

    const handleRightClick = useCallback(() => {
        setCurrentImage(prev => 
            prev === banners.length - 1 ? 0 : prev + 1
        );
    }, [banners.length]);

    useEffect(() => {
        if (banners.length === 0) return;

        const interval = setInterval(() => {
            setCurrentImage(prev => 
                prev === banners.length - 1 ? 0 : prev + 1
            );
        }, 5000);

        return () => clearInterval(interval);
    }, [banners.length]);

    // Show loading or empty state
    if (loading) {
        return (
            <div className='container mx-auto px-4 rounded'>
                <div className='h-56 md:h-72 w-full bg-gradient-to-r from-violet-100 to-pink-100 rounded-lg animate-pulse'></div>
            </div>
        );
    }

    if (banners.length === 0) {
        return (
            <div className='container mx-auto px-4 rounded'>
                <div className='h-56 md:h-72 w-full bg-gradient-to-r from-violet-100 to-pink-100 rounded-lg flex items-center justify-center'>
                    <p className='text-gray-500'>No banners available</p>
                </div>
            </div>
        );
    }

    return (
        <div className='container mx-auto px-4 rounded'>
            <div className='h-56 md:h-72 w-full bg-gradient-to-r from-violet-100 to-pink-100 relative rounded-lg overflow-hidden'>
                {/* Navigation Buttons */}
                {banners.length > 1 && (
                    <div className='absolute z-10 w-full h-full md:flex items-center hidden'>
                        <div className='flex justify-between w-full text-2xl'>
                            <button 
                                className='bg-white shadow-md rounded-full p-1 hover:scale-110 transition-transform' 
                                onClick={handleLeftClick}
                            >
                                <FaAngleLeft />
                            </button>
                            <button 
                                className='bg-white shadow-md rounded-full p-1 hover:scale-110 transition-transform' 
                                onClick={handleRightClick}
                            >
                                <FaAngleRight />
                            </button>
                        </div>                
                    </div>
                )}

                {/* Desktop Images */}
                <div className='hidden md:flex h-full w-full overflow-hidden'>
                    {banners.map((banner, index) => (
                        <div 
                            key={banner._id} 
                            className="absolute w-full h-full transition-transform duration-500 ease-in-out"
                            style={{
                                transform: `translateX(${(index - currentImage) * 100}%)`,
                                opacity: Math.abs(index - currentImage) < 1 ? 1 : 0
                            }}
                        >
                            <img 
                                className='w-full h-full object-cover' 
                                src={banner.desktopImage.url} 
                                alt={`banner-${index + 1}`} 
                            /> 
                            {/* Banner Content Overlay */}
                            {(banner.title || banner.subtitle || banner.ctaText) && (
                                <div className="absolute inset-0 flex items-center justify-start">
                                    <div className="ml-8 md:ml-16 text-white max-w-md">
                                        {banner.title && (
                                            <h2 className="text-2xl md:text-4xl font-bold mb-2 drop-shadow-lg">
                                                {banner.title}
                                            </h2>
                                        )}
                                        {banner.subtitle && (
                                            <p className="text-lg md:text-xl mb-4 drop-shadow-md">
                                                {banner.subtitle}
                                            </p>
                                        )}
                                        {banner.ctaText && (
                                            <a 
                                                href={banner.ctaLink || '#'}
                                                className="inline-block bg-white text-violet-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                                            >
                                                {banner.ctaText}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-pink-600 opacity-15"></div>            
                        </div>
                    ))}
                </div>

                {/* Mobile Images */}
                <div className='flex h-full w-full overflow-hidden md:hidden'>
                    {banners.map((banner, index) => (
                        <div 
                            key={banner._id} 
                            className="absolute w-full h-full transition-transform duration-500 ease-in-out"
                            style={{
                                transform: `translateX(${(index - currentImage) * 100}%)`,
                                opacity: Math.abs(index - currentImage) < 1 ? 1 : 0
                            }}
                        >
                            <img 
                                className='w-full h-full object-cover' 
                                src={banner.mobileImage.url} 
                                alt={`banner-${index + 1}`} 
                            /> 
                            {/* Mobile Banner Content Overlay */}
                            {(banner.title || banner.subtitle || banner.ctaText) && (
                                <div className="absolute inset-0 flex items-center justify-center text-center">
                                    <div className="text-white max-w-xs">
                                        {banner.title && (
                                            <h2 className="text-xl font-bold mb-2 drop-shadow-lg">
                                                {banner.title}
                                            </h2>
                                        )}
                                        {banner.subtitle && (
                                            <p className="text-sm mb-4 drop-shadow-md">
                                                {banner.subtitle}
                                            </p>
                                        )}
                                        {banner.ctaText && (
                                            <a 
                                                href={banner.ctaLink || '#'}
                                                className="inline-block bg-white text-violet-600 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors"
                                            >
                                                {banner.ctaText}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-pink-600 opacity-15"></div>            
                        </div>
                    ))}
                </div>

                {/* Indicator Dots */}
                {banners.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                        {banners.map((_, index) => (
                            <button
                                key={index}
                                className={`w-3 h-3 rounded-full transition-all ${
                                    index === currentImage 
                                        ? 'bg-white scale-125' 
                                        : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                                }`}
                                onClick={() => setCurrentImage(index)}
                            />
                        ))}
                    </div>
                )}
            </div>        
        </div>
    );
}

export default BannerProduct;